import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Grid, useTheme, Fade, useMediaQuery } from '@mui/material';
import CheckoutForm from './CheckoutForm';
import OrderSummary from './OrderSummary';
import { useTranslation } from 'react-i18next';
import { useCart, calculateDistance } from '../CartContext';
import { getUserLocations } from '../userLocationsApi';
import { fetchBranchById } from '../branchesApi';

// ✅ NEW: minimum delivery fee floor in YER (matches mobile)
const MIN_DELIVERY_FEE_YER = 500;

const CheckoutPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const { selectedBranch } = useCart();

  const [deliveryFee, setDeliveryFee] = useState(0); // always in YER
  const [distance, setDistance] = useState(0);
  const [hasLocations, setHasLocations] = useState(false);
  const [branchData, setBranchData] = useState(null);

  // Coupon state
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState('');

  const handleCouponApplied = (couponData) => {
    if (couponData) {
      setCouponDiscount(couponData.discountAmount || 0);
      setCouponCode(couponData.code || '');
    } else {
      setCouponDiscount(0);
      setCouponCode('');
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setTimeout(() => {
      const scrollAmount = Math.floor(window.innerHeight * (isMobile ? 0.15 : 0.3));
      let start = window.scrollY;
      let end = scrollAmount;
      let duration = isMobile ? 800 : 1200;
      let startTime = null;

      function animateScroll(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease =
          progress < 0.5
            ? 2 * progress * progress
            : -1 + (4 - 2 * progress) * progress;
        window.scrollTo(0, start + (end - start) * ease);
        if (progress < 1) window.requestAnimationFrame(animateScroll);
      }
      window.requestAnimationFrame(animateScroll);
    }, 400);
  }, [isMobile]);

  useEffect(() => {
    const fetchFreshBranchData = async () => {
      if (selectedBranch?.id) {
        try {
          const freshBranch = await fetchBranchById(selectedBranch.id);
          setBranchData(freshBranch);
        } catch (error) {
          console.error('Error fetching branch data:', error);
          setBranchData(selectedBranch);
        }
      } else {
        setBranchData(selectedBranch);
      }
    };
    fetchFreshBranchData();
  }, [selectedBranch?.id, selectedBranch]);

  useEffect(() => {
    const calculateFee = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return;

        const user = JSON.parse(userStr);
        const userId = user.Id || user.id;

        if (!branchData) return;
        if (!userId) return;

        const locations = await getUserLocations(userId);
        setHasLocations(locations && locations.length > 0);

        if (branchData && branchData.latitude && branchData.longitude) {
          const defaultLoc =
            locations.find((l) => l.IsDefault) || locations[0];

          if (defaultLoc && defaultLoc.Latitude && defaultLoc.Longitude) {
            const dist = calculateDistance(
              defaultLoc.Latitude,
              defaultLoc.Longitude,
              branchData.latitude,
              branchData.longitude
            );
            setDistance(dist);

            const feeType =
              branchData.deliveryFeeType ||
              branchData.deliveryfeetype ||
              branchData.DeliveryFeeType ||
              'fixedAmount';
            const feeValue = Number(
              branchData.deliveryFeeValue ||
                branchData.deliveryfeevalue ||
                branchData.DeliveryFeeValue ||
                0
            );
            const rangeKm = Number(
              branchData.DeliveryRangeKm ||
                branchData.deliveryRangeKm ||
                branchData.deliveryrangekm ||
                10
            );

            // ✅ Calculate fee in YER first
            let calculatedFeeYER = 0;

            if (feeType === 'byKm') {
              if (dist <= rangeKm) {
                calculatedFeeYER = dist * feeValue;
              } else {
                calculatedFeeYER = rangeKm * feeValue + 500;
              }
            } else {
              if (dist <= rangeKm) {
                calculatedFeeYER = feeValue;
              } else {
                calculatedFeeYER = feeValue + 500;
              }
            }

            // ✅ NEW: enforce 500 YER floor when there is an actual fee
            if (
              calculatedFeeYER > 0 &&
              calculatedFeeYER < MIN_DELIVERY_FEE_YER
            ) {
              calculatedFeeYER = MIN_DELIVERY_FEE_YER;
            }

            setDeliveryFee(calculatedFeeYER);
          } else {
            // No location chosen yet → fee stays 0 (no fake floor)
            setDeliveryFee(0);
            setDistance(0);
          }
        } else {
          // No branch coordinates → fall back, but still respect floor only when > 0
          setDeliveryFee(0);
          setDistance(0);
        }
      } catch (error) {
        console.error('Error calculating delivery fee:', error);
        setDeliveryFee(0);
      }
    };

    if (branchData) calculateFee();
  }, [branchData]);

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: { xs: 4, sm: 6, md: 8 },
        direction: i18n.dir(),
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2, md: 4, lg: 6 } }}>
        <Box sx={{ mb: { xs: 4, sm: 5, md: 6 }, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
              letterSpacing: -0.5,
              color: 'text.primary',
              fontFamily: 'Sarabun, sans-serif',
              mb: { xs: 1, sm: 1.5 },
            }}
          >
            {t('checkout.title')}
          </Typography>
          <Box
            sx={{
              width: { xs: 50, sm: 60 },
              height: { xs: 3, sm: 4 },
              bgcolor: 'primary.main',
              borderRadius: 2,
              mx: 'auto',
            }}
          />
        </Box>

        <Fade in={true} timeout={800}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3, md: 4 }}
            justifyContent="center"
          >
            <Grid item xs={12} md={5} lg={5}>
              <Box
                sx={{
                  position: { xs: 'relative', md: 'sticky' },
                  top: { md: 24 },
                  height: 'fit-content',
                  zIndex: 1,
                }}
              >
                <OrderSummary
                  deliveryFee={deliveryFee}
                  distance={distance}
                  couponDiscount={couponDiscount}
                  couponCode={couponCode}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={7} lg={7}>
              <CheckoutForm
                deliveryFee={deliveryFee}
                distance={distance}
                hasLocations={hasLocations}
                onCouponApplied={handleCouponApplied}
              />
            </Grid>
          </Grid>
        </Fade>
      </Container>
    </Box>
  );
};

export default CheckoutPage;